// Client-side photo compression (Onda 2a). Native canvas, zero deps.
// Never degrades or drops a photo: the original passes through on any decode
// failure or when the "compressed" result isn't actually smaller.

// Pure: fit (w,h) within a max longest-edge, preserving aspect ratio.
export function fitDimensions(w, h, maxEdge) {
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return { w, h, scaled: false };
  const s = maxEdge / longest;
  return { w: Math.round(w * s), h: Math.round(h * s), scaled: true };
}

function renamed(name, type) {
  return String(name || 'photo').replace(/\.[^.]+$/, '') + (type === 'image/png' ? '.png' : '.jpg');
}

export async function compressImage(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? 1600;
  const quality = opts.quality ?? 0.8;
  // 'image/png' keeps transparency (a logo); photos stay JPEG.
  const type = opts.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const looksImage = !!file && (String(file.type).startsWith('image/')
    || /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name || ''));
  if (!looksImage) return file;

  let bmp;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file; // undecodable (e.g. HEIC w/o support) — keep original
  }
  try {
    const { w, h } = fitDimensions(bmp.width, bmp.height, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise(res => canvas.toBlob(res, type, quality));
    if (!blob || blob.size >= file.size) return file; // no gain — keep original
    return new File([blob], renamed(file.name, type),
      { type, lastModified: file.lastModified || Date.now() });
  } catch {
    return file;
  } finally {
    bmp.close?.();
  }
}
